import { Component, Input, OnDestroy, OnInit, signal } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { WellType } from 'src/app/shared/models/wellType.model';
import { WellTypeService } from '../../services/well-type-datums.service';
import { UserUnitsModel } from 'src/app/core/components/user-units/user-units.model';
import { catchError } from 'rxjs/operators';
import { StorageKeys, StoreService } from 'src/app/core/services/store.service';
import { lastValueFrom, Subscription, throwError } from 'rxjs';
import { MediatorService } from 'src/app/shared/services/mediator.service';
import { DataSavedMessage } from 'src/app/shared/models/mediator-messages.model';
import { DialogService } from 'primeng/dynamicdialog';
import { ConfirmationService, PrimeIcons } from 'primeng/api';
import { wellheadElevationAboveDfe } from 'src/app/core/components/well-explorer/validation/wellheadElevationAboveDfe';
import { wellheadDepthAboveMudline } from 'src/app/core/components/well-explorer/validation/wellheadDepthAboveMudline';
import { PeriforOnChangeMessages, SignalRService } from 'src/app/shared/services/signal-r.service';
import { handleValidationError } from 'src/app/shared/helpers/validation-error.helper';

@Component({
  selector: 'app-well-type-datums',
  templateUrl: './well-type-datums.component.html',
  standalone: false,
  providers: [DialogService, ConfirmationService],
})
export class WellTypeDatumsComponent implements OnInit, OnDestroy {
  public control(name: string): UntypedFormControl {
    return this.wellTypeForm.get(name) as UntypedFormControl;
  }

  public wellTypeForm: UntypedFormGroup;
  public wellTypes: { label: string, value: string }[];
  public wellheadType: string;
  public depthRelativeTo: string;
  public wellheadElevationRelativeTo: string;
  public waterDepthVisible: boolean;
  public mudlineDepth: number;
  public isLoading = signal<boolean>(false);
  public lengthUnit: string;
  private _subscriptions: Subscription = new Subscription();
  public dfeError: string;
  public validationErrors = signal<string[]>([]);
  public depthValidation = { min: 0, max: Number.MAX_VALUE };

  @Input()
  private componentId: string;

  constructor(
    private _wellTypeService: WellTypeService,
    private _formBuilder: UntypedFormBuilder,
    private _store: StoreService,
    private _messenger: MediatorService,
    private _confirmationService: ConfirmationService,
    private _signalRService: SignalRService
  ) {
    this.isLoading.set(true);
    this.wellTypes = [
      { label: 'Land', value: 'Land' },
      { label: 'Platform', value: 'Platform' },
      { label: 'Subsea', value: 'Subsea' }
    ];
    this.createReactiveForm();
  }

  async ngOnInit(): Promise<void> {
    this.lengthUnit = (await this._store.get<UserUnitsModel>(StorageKeys.UNITS)).longLengths;

    this.getWellTypeDataFromForm();

    const hub = this._signalRService.getConnectionToNotificationHub();
    this._signalRService.subscribeToEventFilteredByDesignId(hub, SignalRService.ON_PFB_CHANGE, d => this.signalRfunc(d));
  }

  private signalRfunc(data: any): void {
    if (data.action === PeriforOnChangeMessages.REFRESH_DATUMS) {
      this.getWellTypeDataFromForm();
    }
  }

  private getWellTypeDataFromForm(): void {
    this._wellTypeService.getWellType()
      .pipe(
        catchError(err => {
          this.isLoading.set(false);
          return throwError(() => err); // Ensure it returns an observable error
        })
      )
      .subscribe(data => {
        this.isLoading.set(true);
        const wellType = new WellType(data);
        delete wellType.wellheadDepthFromDrillFloor;

        this.wellTypeForm.setValue(wellType, { emitEvent: false });
        this.onTypeChange(wellType.type);

        const wellheadDepth = data.wellheadElevation >= 0 ? data.wellheadElevation : data.wellheadElevation;
        this.wellTypeForm.controls.wellheadElevation.setValue(wellheadDepth, { emitEvent: false });

        this.mudlineDepth = +(data.drillFloorElevation + data.waterDepth).toFixed(2);
        this.isLoading.set(false);
      });
  }

  private createReactiveForm(): void {
    this.wellTypeForm = this._formBuilder.group({
      type: new UntypedFormControl({ value: 'Land', disabled: true }),
      drillFloorElevation: new UntypedFormControl({ value: 0, disabled: true }, { validators: [Validators.min(this.depthValidation.min), Validators.max(this.depthValidation.max), Validators.required], updateOn: 'blur' }),
      wellheadElevation: new UntypedFormControl({ value: 0, disabled: true }, { validators: [Validators.min(this.depthValidation.min), Validators.max(this.depthValidation.max), Validators.required], updateOn: 'blur' }),
      wellheadDepth: new UntypedFormControl({ value: 0, disabled: true }, { validators: [Validators.min(this.depthValidation.min), Validators.max(this.depthValidation.max), Validators.required], updateOn: 'blur' }),
      waterDepth: new UntypedFormControl({ value: 100, disabled: true }, { validators: [Validators.min(1), Validators.max(this.depthValidation.max), Validators.required], updateOn: 'blur' }),
    });

    // Subscribe to drill floor elevation changes
    this._subscriptions.add(
      this.wellTypeForm.get('drillFloorElevation').valueChanges.subscribe(async (value) => {
        if (this.wellTypeForm.get('drillFloorElevation').valid) {
          try {
            await lastValueFrom(this._wellTypeService.updateDrillFloorDatum(value));
            this.mudlineDepth = +(value + this.wellTypeForm.controls.waterDepth.value).toFixed(2);
            this._messenger.publish(new DataSavedMessage('Well Type & Datums'));
            this.wellTypeForm.get('drillFloorElevation').disable({ emitEvent: false });
            this.validationErrors.set([]);
          } catch (err: any) {
            handleValidationError(err, this.validationErrors, this.wellTypeForm.get('drillFloorElevation'));
          }
        }
      })
    );

    // Subscribe to water depth changes
    this._subscriptions.add(
      this.wellTypeForm.get('waterDepth').valueChanges.subscribe(async (value) => {
        if (this.wellTypeForm.get('waterDepth').valid) {
          try {
            await lastValueFrom(this._wellTypeService.updateWaterDepth(value));
            this.mudlineDepth = +(this.wellTypeForm.controls.drillFloorElevation.value + value).toFixed(2);
            this._messenger.publish(new DataSavedMessage('Well Type & Datums'));
            this.wellTypeForm.get('waterDepth').disable({ emitEvent: false });
            this.validationErrors.set([]);
          } catch (err: any) {
            handleValidationError(err, this.validationErrors, this.wellTypeForm.get('waterDepth'));
          }
        }
      })
    );
  }

  private onTypeChange(value: string) {
    if (value === 'Land') {
      this.onOnshoreSelect();
    } else if (value === 'Platform') {
      this.onPlatformSelect();
    } else {
      this.onSubseaSelect();
    }
  }

  public editField(fieldName: string) {
    const control = this.wellTypeForm.get(fieldName);
    if (control && !control.disabled) {
      return;
    }
    const fieldNameFormatted = fieldName
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, c => c.toUpperCase());

    this._confirmationService.confirm({
      message: `Changing the ${fieldNameFormatted} will adjust every MD and TVD in the design by the difference between the new and old ${fieldNameFormatted}. <br><br>
      We suggest creating a backup (copy or export) of your design before proceeding. <br><br>
      Are you sure that you want to continue?`,
      header: 'Confirmation',
      icon: PrimeIcons.EXCLAMATION_TRIANGLE,
      accept: () => {
        control.enable({ emitEvent: false });
        control.updateValueAndValidity({ emitEvent: false });
      }
    });
  }

  private onOnshoreSelect() {
    this.waterDepthVisible = false;
    this.wellheadType = 'Wellhead Elevation';
    this.depthRelativeTo = ` (${this.lengthUnit} above ground level)`;
    this.wellheadElevationRelativeTo = ` (${this.lengthUnit} above ground level)`;

    this.wellTypeForm.controls.drillFloorElevation.setValidators(wellheadElevationAboveDfe);
    this.dfeError = `Minimum value: ${this.wellTypeForm.controls.wellheadElevation.value} ${this.lengthUnit}`;
  }

  private onPlatformSelect() {
    this.waterDepthVisible = true;
    this.wellheadType = 'Wellhead Elevation';
    this.depthRelativeTo = `(${this.lengthUnit} above MSL)`;
    this.wellheadElevationRelativeTo = ` (${this.lengthUnit} above MSL)`;

    if (!this.wellTypeForm.controls.waterDepth.value) {
      this.wellTypeForm.controls.waterDepth.setValue(100);
    }
    // this.wellTypeForm.controls.wellheadElevation.setValidators(wellheadElevationAboveDfe);
    this.wellTypeForm.controls.drillFloorElevation.setValidators(wellheadElevationAboveDfe);
    this.dfeError = `Minimum value: ${this.wellTypeForm.controls.wellheadElevation.value} ${this.lengthUnit}`;
  }

  private onSubseaSelect() {
    this.waterDepthVisible = true;
    this.wellheadType = 'Wellhead Depth';
    this.depthRelativeTo = ` (${this.lengthUnit} above MSL)`;
    this.wellheadElevationRelativeTo = ` (${this.lengthUnit} below MSL)`;

    if (!this.wellTypeForm.controls.waterDepth.value) {
      this.wellTypeForm.controls.waterDepth.setValue(100);
    }

    // this.wellTypeForm.controls.waterDepth.setValidators(wellheadDepthAboveMudline);
  }

  ngOnDestroy(): void {
    this._subscriptions.unsubscribe();
    this.signalRfunc = null;
  }
}
